/**
 * Social Connect utilities.
 *
 * Allow users to link their social media profiles.
 * Linked profiles shown on user profile page for trust.
 *
 * Social links stored in user.publicData.socialLinks = {
 *   instagram: 'https://instagram.com/username',
 *   linkedin: 'https://linkedin.com/in/username',
 *   twitter: 'https://twitter.com/username',
 *   facebook: 'https://facebook.com/username',
 *   website: 'https://example.com'
 * }
 *
 * Only HTTPS links are allowed.
 */

export const SUPPORTED_PLATFORMS = ['instagram', 'linkedin', 'twitter', 'facebook', 'youtube', 'tiktok', 'website'];

/**
 * Validate a social link URL.
 * @param {string} platform
 * @param {string} url
 * @returns {string|null} — error or null
 */
export const validateSocialLink = (platform, url) => {
  if (!SUPPORTED_PLATFORMS.includes(platform)) return 'Unsupported platform';
  if (!url?.trim()) return null; // empty = remove link
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return 'URL must use HTTPS';
  } catch (_) {
    return 'Invalid URL';
  }

  const platformDomains = {
    instagram: 'instagram.com',
    linkedin: 'linkedin.com',
    twitter: ['twitter.com', 'x.com'],
    facebook: 'facebook.com',
    youtube: 'youtube.com',
    tiktok: 'tiktok.com',
  };

  const domain = platformDomains[platform];
  if (domain) {
    const url_parsed = new URL(url);
    const domains = Array.isArray(domain) ? domain : [domain];
    if (!domains.some(d => url_parsed.hostname === d || url_parsed.hostname.endsWith(`.${d}`))) {
      return `URL must be from ${domains.join(' or ')}`;
    }
  }
  return null;
};

/**
 * Get social links from user.
 * @param {Object|null} user
 * @returns {Object}
 */
export const getSocialLinks = (user) =>
  user?.attributes?.profile?.publicData?.socialLinks || {};

/**
 * Get platform icon identifier.
 * @param {string} platform
 * @returns {string}
 */
export const getPlatformIcon = (platform) =>
  SUPPORTED_PLATFORMS.includes(platform) ? platform : 'link';
